import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { colours, sizes } from '@qflow/theme'

import {
  transformationMessages,
  getCustomTransformationMessages
} from 'portal/components/RecordDetails/SharedComponents/valueAddHelpers'

import { useCountryDrivenValues, useModuleIds } from 'portal/lib/hooks'
import {
  AI,
  ChevronDown,
  Document,
  PersonSolid
} from 'portal/lib/primitives/icons'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import { recordHistoryFieldNameLabelMapper } from './recordHistoryFieldNameMappers'

export function RecordHistoryFieldDetails({ fieldName, historyObject }) {
  const [openIndicator, setOpenIndicator] = useState(false)
  const { accountId, projectId, moduleName, recordId } = useModuleIds()
  const { text: overwriteText } = useCountryDrivenValues()

  const dispatch = useDispatch()
  const projectTimeZone = useSelector(
    (state: any) => state.projectDetails.modules?.timeZone
  )
  const projectUsers = useSelector((state: any) => state.projectUsers)

  const labelMapper = recordHistoryFieldNameLabelMapper(overwriteText)

  type User = {
    userId: string
    displayName: string
  }

  const users: User[] = Object.values(projectUsers.users)

  let activeChange = 'raw'
  if (historyObject.clientEdit) {
    activeChange = 'clientEdit'
  } else if (historyObject.qflow) {
    activeChange = 'qflow'
  }

  const getFormattedText = (type: string) => {
    if (type === 'qflow') {
      if (fieldName && historyObject.qflow.sourceType) {
        const customMessage = getCustomTransformationMessages(
          fieldName,
          historyObject.qflow.sourceType
        )
        if (customMessage) {
          return customMessage
        }
      }
      if (
        historyObject.qflow.sourceType &&
        transformationMessages[historyObject.qflow.sourceType]
      ) {
        return transformationMessages[historyObject.qflow.sourceType]
      }
    }

    if (
      type === 'clientEdit' &&
      historyObject.clientEdit &&
      historyObject.clientEdit.userId &&
      historyObject.clientEdit.timestamp
    ) {
      const user = users.find(
        user => user.userId === historyObject.clientEdit.userId
      )

      return `${user?.displayName ? `${user.displayName}: ` : ''} ${new Date(
        historyObject.clientEdit.timestamp
      ).toLocaleString([], {
        timeZone: projectTimeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })}`
    } else if (
      type === 'clientEdit' &&
      historyObject.clientEdit &&
      !historyObject.clientEdit.userId
    ) {
      return 'System'
    }
    return ''
  }

  const getFormattedValue = (value: string) => {
    if (fieldName.endsWith('Date') || fieldName.endsWith('Time')) {
      return new Date(value).toLocaleString([], {
        timeZone: projectTimeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      })
    }
    return value
  }

  const handleToggle = event => {
    setOpenIndicator(event.target.open)

    const EV_HISTORY_FILED_EXPANDED = 'frontend_records_history_field_expanded'
    if (event.target.open) {
      dispatch(
        trackMixpanel(EV_HISTORY_FILED_EXPANDED, {
          project_id: projectId,
          account_id: accountId,
          moduleName: moduleName?.toLowerCase(),
          record_id: recordId,
          field_name: moduleName
            ? labelMapper[moduleName.toUpperCase()][fieldName] || fieldName
            : fieldName
        })
      )
    }

    // Prevent the click event from bubbling up
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <StyledDetails onToggle={handleToggle}>
      <summary
        onClick={() => {
          setOpenIndicator(prev => !prev)
        }}
      >
        {openIndicator ? (
          <ChevronDown size={'8'} rotate={180} />
        ) : (
          <ChevronDown size={'8'} />
        )}
        {moduleName
          ? labelMapper[moduleName.toUpperCase()][fieldName]
          : fieldName}
      </summary>
      <ChangesWrapper>
        <Change style={{ borderTop: 'none', paddingTop: 0 }}>
          <IconWrapper $active={activeChange === 'clientEdit'}>
            <PersonSolid />
          </IconWrapper>
          <ChangeDetails $active={activeChange === 'clientEdit'}>
            <div>
              {historyObject.clientEdit
                ? getFormattedText('clientEdit')
                : 'Client'}
            </div>
            <div>
              {historyObject.clientEdit
                ? getFormattedValue(historyObject.clientEdit.value)
                : 'No change'}
            </div>
          </ChangeDetails>
        </Change>
        <Change>
          <IconWrapper $active={activeChange === 'qflow'}>
            <AI size={sizes.SMALL_MEDIUM} />
          </IconWrapper>
          <ChangeDetails $active={activeChange === 'qflow'}>
            <div>
              Qflow{historyObject.qflow ? `: ${getFormattedText('qflow')}` : ''}
            </div>
            <div>
              {historyObject.qflow
                ? getFormattedValue(historyObject.qflow.value)
                : 'No change'}
            </div>
          </ChangeDetails>
        </Change>
        <Change>
          <IconWrapper $active={activeChange === 'raw'}>
            <Document />
          </IconWrapper>
          <ChangeDetails $active={activeChange === 'raw'}>
            <div>Original</div>
            <div>
              {historyObject.raw ? historyObject.raw : 'No data captured'}
            </div>
          </ChangeDetails>
        </Change>
      </ChangesWrapper>
    </StyledDetails>
  )
}

export function RecordHistoryItemWrapperDetails({
  children,
  item,
  moduleName
}) {
  const [openIndicator, setOpenIndicator] = useState(false)
  const [showCloseAll, setShowCloseAll] = useState(false)

  const name =
    moduleName.toLowerCase() === 'delivery' ? 'name' : 'knownContainerType'

  const handleToggle = event => {
    setOpenIndicator(event.target.open)

    // Prevent the click event from bubbling up
    event.preventDefault()
    event.stopPropagation()
  }

  const openAllDetails = (event: any) => {
    const groupTitleElement = event.target.parentElement

    // First ensure that the group is open
    groupTitleElement.parentElement.open = true
    setOpenIndicator(true)

    // Then open all the details
    const fieldGroup = groupTitleElement.nextElementSibling
    fieldGroup.querySelectorAll('details').forEach(detail => {
      detail.open = true
    })

    setShowCloseAll(true)

    // Prevent the click event from bubbling up
    event.preventDefault()
    event.stopPropagation()
  }

  const closeAllDetails = (event: any) => {
    const groupTitleElement = event.target.parentElement

    // First ensure that the group is close
    groupTitleElement.parentElement.open = false
    setOpenIndicator(false)

    // Then close all the details
    const fieldGroup = groupTitleElement.nextElementSibling
    fieldGroup.querySelectorAll('details').forEach(detail => {
      detail.open = false
    })

    setShowCloseAll(false)

    // Prevent the click event from bubbling up
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <StyledDetails
      style={{ background: 'transparent' }}
      onToggle={handleToggle}
    >
      <summary
        onClick={() => {
          setOpenIndicator(prev => !prev)
        }}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
          {openIndicator ? (
            <ChevronDown size={'8'} rotate={180} />
          ) : (
            <ChevronDown size={'8'} />
          )}
          {item[name].clientEdit?.value
            ? item[name].clientEdit.value
            : item[name].qflow?.value
              ? item[name].qflow.value
              : item[name].raw}
        </div>
        {showCloseAll ? (
          <OpenCloseAll onClick={closeAllDetails}>Close all</OpenCloseAll>
        ) : (
          <OpenCloseAll onClick={openAllDetails}>Open all</OpenCloseAll>
        )}
      </summary>
      <ChildrenWrapper>{children}</ChildrenWrapper>
    </StyledDetails>
  )
}

const StyledDetails = styled.details`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
  margin: 0;
  border-bottom: 1px solid ${colours.CLOUD_GREY};
  background: none;
  list-style: none;
  font-size: 14px;
  font-weight: 700;
  outline: none;
  user-select: none;
  padding: 0 0.75rem;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover > SUMMARY {
    color: ${colours.TEAL};
  }

  &[open] {
    background: ${colours.CONTROL_PANEL_GREY};
  }

  & > SUMMARY {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
    color: ${colours.CORE_NAVY};
    font-weight: 500;
  }

  &[open] > SUMMARY {
    color: ${colours.TEAL};
  }
`
const ChangesWrapper = styled.div`
  margin-top: 0.25rem;
`
const Change = styled.div`
  border-top: 1px solid ${colours.CLOUD_GREY};
  padding: 0.5rem 0;
  display: flex;
  gap: 0.625rem;
`
const IconWrapper = styled.div<{ $active: boolean }>`
  margin-top: 0.125rem;
  color: ${({ $active }) => ($active ? colours.TEAL : colours.BLUEY_GREY)};
  background-color: ${({ $active }) =>
    $active ? colours.CORE_NAVY : colours.WHITE_10};
  display: flex;
  flex-shrink: 0;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
`
const ChangeDetails = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 400;
  color: ${({ $active }) =>
    $active ? colours.CORE_NAVY : colours.HIGHLIGHTED_NAVY};

  & > div:first-of-type {
    font-weight: 700;
  }
`
const ChildrenWrapper = styled.div`
  margin-bottom: 0.75rem;
  border-radius: 10px;

  & details:first-of-type {
    border-radius: 10px 10px 0 0;
  }

  & details:last-of-type {
    border-radius: 0 0 10px 10px;
  }
`
const OpenCloseAll = styled.div`
  font-size: 12px;
  color: ${colours.LIGHT_NAVY};
  cursor: pointer;
  font-weight: 400;
  flex-shrink: 0;
`
